import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useTranslation } from 'next-i18next'

import type { PLPageMeta } from '@src/components/pl-page-context'
import { HomePagePLRenderer } from '@src/pages/home-pl/home-page-pl-renderer'
import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import { isMobileDevice } from '@src/quince-site-common/src/helpers/getDeviceTypeForPageRouter'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import type { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'
import { FEATURE_FLAG_TYPE } from '@src/server-side/server-feature-flags/feature-flag.types'

import { Home } from './[...slug]/components/homepage'
import { HomepageFeatureProvider } from './[...slug]/components/homepage/homepage-feature-provider'
import Layout from './layout'

export const getServerSideProps = (async ctx => {
  const serverSideBaseProps = await getServerSideBaseProps(ctx, PAGE_PATH.HOME)
  return serverSideBaseProps
}) satisfies GetServerSideProps<ServerSideBaseProps>

/** Build a minimal PLPageMeta for the home PL renderer from server props (mirrors the PDP template). */
function buildPageMetaForPL(plData: PLAPIResponse, pathname: string, zipCode?: string | null): PLPageMeta {
  const metaSlug = plData.meta?.slug ?? pathname
  let seoData: PLPageMeta['seoData'] = null
  if (plData.meta?.seoData) {
    seoData = {
      ...plData.meta.seoData,
      slug: metaSlug,
    }
  }

  return {
    title: plData.meta?.title ?? null,
    pathname,
    searchParams: '',
    seoData,
    zipCode: zipCode ?? null,
    context: plData.context,
    // Drives the v2 lazy-batch `pageType` (→ HOME) and observability tagging.
    // eslint-disable-next-line i18next/no-literal-string -- page-type identifier, not user copy
    pageType: 'home',
  }
}

export default function PageHome(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation('common')
  const homeFeatureFlags =
    props.serverSideFeatureFlags?.featureFlagType === FEATURE_FLAG_TYPE.HOME ? props.serverSideFeatureFlags : null

  // 1.3.1: the homepage outline started at h2, so screen readers had no page topic to identify.
  // Visually hidden rather than promoting a hero heading — that would change rendered type sizes.
  // Rendered in both branches below because the PL and non-PL paths return separately.
  const pageHeading = <h1 className="sr-only">{t('quince')}</h1>

  // PL path: render via the PL renderer whenever the server flags this page as PL format.
  if (props.pageData?.context?.isPLFormat) {
    const plData = props.pageData.context.pageDataJson as unknown as PLAPIResponse
    const pageMeta = buildPageMetaForPL(plData, props.pathName, props.zipCodeInfo?.zipCode)
    return (
      <Layout {...props}>
        {pageHeading}
        <HomepageFeatureProvider serverSideFeatureFlags={homeFeatureFlags}>
          <HomePagePLRenderer plData={plData} pageMeta={pageMeta} serverSideFeatureFlags={homeFeatureFlags} />
        </HomepageFeatureProvider>
      </Layout>
    )
  }

  return (
    <Layout {...props}>
      {pageHeading}
      {props.pageData ? (
        <HomepageFeatureProvider serverSideFeatureFlags={homeFeatureFlags}>
          <Home pageContext={props.pageData.context?.pageDataJson} isMobile={isMobileDevice(props.deviceType)} />
        </HomepageFeatureProvider>
      ) : (
        <SomethingWentWrong />
      )}
    </Layout>
  )
}
